<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { createDotField, type DotField } from '../../utils/dotField'
import { isWeChatUA, prefersReducedMotion } from '../../utils/environment'
import { ensureMutedAutoplayUnlock, requestMutedAutoplay } from '../../utils/mutedAutoplay'
import lx from '../../utils/lx'
import { ASSETS, LX_BIDS } from './constants'

const props = defineProps<{
  isMobile: boolean
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const videoRef = ref<HTMLVideoElement | null>(null)
const videoPlaying = ref(false)
let dotField: DotField | undefined
let autoplayCleanup: (() => void) | undefined

function detectSafariDesktop(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /safari/i.test(ua) && !/chrome|chromium|crios|android|edg|MicroMessenger/i.test(ua)
}

/**
 * 移动端优先使用轻量 H.264；桌面 Safari 保留 HEVC Alpha，其它桌面端使用 WebM Alpha。
 */
const heroVideoSrc = computed(() => {
  if (props.isMobile) return ASSETS.heroVideoMobile
  return detectSafariDesktop() ? ASSETS.heroVideoSafari : ASSETS.heroVideo
})

const onVideoEnter = () => {
  if (videoRef.value) videoRef.value.playbackRate = 0.5
}
const onVideoLeave = () => {
  if (videoRef.value) videoRef.value.playbackRate = 1
}

const onLoadedData = () => {
  if (!props.isMobile) return
  const video = videoRef.value
  if (!video) return
  video.muted = true
  void video.play().catch(() => {
    autoplayCleanup?.()
    autoplayCleanup = requestMutedAutoplay(video)
  })
}

const onVideoPlay = () => {
  if (!props.isMobile) return
  videoPlaying.value = true
}

const onVideoPause = () => {
  if (!props.isMobile) return
  videoPlaying.value = false
}

const startDotField = () => {
  if (props.isMobile || !canvasRef.value || prefersReducedMotion()) return
  if (dotField) return
  dotField = createDotField(canvasRef.value)
  dotField.init()
}

const stopDotField = () => {
  dotField?.destroy()
  dotField = undefined
}

onMounted(() => {
  startDotField()
  const video = videoRef.value
  if (!video) return

  // 桌面端保持原有播放逻辑，不挂微信 Bridge/手势解锁监听。
  if (!props.isMobile) {
    void video.play().catch(() => undefined)
    return
  }

  ensureMutedAutoplayUnlock()
  video.muted = true
  void video.play().catch(() => {
    autoplayCleanup = requestMutedAutoplay(video)
  })
  if (isWeChatUA()) {
    autoplayCleanup?.()
    autoplayCleanup = requestMutedAutoplay(video)
  }
})

watch(
  () => props.isMobile,
  (mobile) => {
    if (mobile) stopDotField()
    else startDotField()
  }
)

onBeforeUnmount(() => {
  stopDotField()
  autoplayCleanup?.()
})
</script>

<template>
  <section id="top" class="hero" :data-mv-bid="LX_BIDS.heroMv" data-mv-key="hero">
    <div v-if="!isMobile" class="hero-dot-layer" aria-hidden="true">
      <canvas ref="canvasRef" class="life-bg-canvas" />
    </div>
    <div class="hero-inner">
      <div class="hero-copy">
        <div class="hero-kicker">@ MEITUAN AI LIVE</div>
        <template v-if="isMobile">
          <div class="hero-title-1 hero-title-plain">美团智播数字人</div>
          <div class="hero-title-2-wrap">
            <span class="hero-title-2 hero-title-plain">本地生活直播解决方案</span>
          </div>
        </template>
        <template v-else>
          <div class="hero-title-1">
            <span class="type-text" data-text="美团智播数字人" />
            <img class="cursor-blink" data-line="1" :src="ASSETS.cursor" alt="">
          </div>
          <div class="hero-title-2-wrap">
            <span class="hero-title-2">
              <span class="type-text" data-text="本地生活直播解决方案" />
            </span>
            <img class="cursor-blink cursor-hidden" data-line="2" :src="ASSETS.cursor" alt="">
          </div>
        </template>
        <div class="hero-tags">
          <span>快速开播</span>
          <span class="sep">/</span>
          <span>智能讲解</span>
          <span class="sep">/</span>
          <span>全天在线</span>
          <span class="sep">/</span>
          <span>让直播经营更简单</span>
        </div>
        <a class="btn-cta btn-cta-hidden" href="#capSection" @click="lx.moduleClick(LX_BIDS.heroMc)">
          能力呈现
          <img class="btn-cta-arrow" :src="ASSETS.btnArrow" alt="">
        </a>
      </div>
      <div
        class="hero-media"
        aria-hidden="true"
        @mouseenter="onVideoEnter"
        @mouseleave="onVideoLeave"
      >
        <img
          v-if="isMobile"
          class="hero-poster"
          :class="{ 'is-hidden': videoPlaying }"
          :src="ASSETS.heroPoster"
          alt=""
          aria-hidden="true"
        >
        <video
          ref="videoRef"
          class="hero-video"
          :src="heroVideoSrc"
          :poster="isMobile ? ASSETS.heroPoster : undefined"
          autoplay
          muted
          loop
          playsinline
          :webkit-playsinline="isMobile ? '' : undefined"
          :x5-playsinline="isMobile ? '' : undefined"
          :x5-video-player-type="isMobile ? 'h5' : undefined"
          preload="auto"
          disablepictureinpicture
          disableremoteplayback
          @loadeddata="onLoadedData"
          @playing="onVideoPlay"
          @pause="onVideoPause"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.hero {
  position: relative;
  height: 100vh;
  min-height: 820px;
  overflow: hidden;
  background: #fff;
}

.hero-dot-layer {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.28) 0%,
    rgba(0, 0, 0, 0.55) 48px,
    #000 120px,
    #000 calc(100% - 100px),
    rgba(0, 0, 0, 0.45) 100%
  );
  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.28) 0%,
    rgba(0, 0, 0, 0.55) 48px,
    #000 120px,
    #000 calc(100% - 100px),
    rgba(0, 0, 0, 0.45) 100%
  );
}

.life-bg-canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.hero-inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 1200px;
  max-width: calc(100% - 48px);
  height: 100%;
  margin: 0 auto;
  padding: 120px 0 64px;
}

.hero-copy {
  position: relative;
  z-index: 2;
  width: 600px;
  max-width: 100%;
}

.hero-kicker {
  margin: 0 0 12px;
  color: #8932ff;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
}

.hero-title-1,
.hero-title-2-wrap {
  display: flex;
  align-items: center;
  font-weight: 650;
  line-height: 1.2;
}

.hero-title-1 {
  margin: 0;
  min-height: 84px;
  font-size: 70px;
  color: transparent;
  background: linear-gradient(98deg, #8932ff 0%, #f49bff 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-title-2-wrap {
  margin-top: 24px;
  margin-bottom: 48px;
  min-height: 72px;
  font-size: 60px;
}

.hero-title-2 {
  color: #000;
  -webkit-text-fill-color: #000;
  background: none;
}

.hero-title-plain {
  color: #171717 !important;
  -webkit-text-fill-color: #171717 !important;
  background: none !important;
}

.hero-title-1.hero-title-plain {
  background: linear-gradient(98deg, #8932ff 0%, #f49bff 100%) !important;
  background-clip: text !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
}

.cursor-blink {
  flex: none;
  width: 32px;
  height: 1.1em;
  margin-left: 6px;
  object-fit: contain;
  animation: blink 0.5s ease-in-out infinite;
}

.cursor-hidden {
  visibility: hidden;
}

.hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 13px;
  margin: 0 0 80px;
  color: #929292;
  font-size: 18px;
  line-height: normal;
}

.hero-tags .sep {
  color: #929292;
}

.btn-cta {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 202px;
  height: 61px;
  padding: 0 24px;
  border-radius: 518px;
  background: #000;
  color: #fff;
  font-size: 28px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 0 0 0 #1e00ff, 0 0 0 0 #1e00ff, 0 0 0 0 #1e00ff;
  transition:
    background 0.2s,
    transform 0.4s ease,
    opacity 0.4s ease,
    box-shadow 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);

  &::before {
    content: '';
    position: absolute;
    inset: -4px -16px;
    border-radius: 100px;
    background: #7b2fff;
    z-index: -1;
    opacity: 0;
    transform: scale(0.6);
    transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.35s ease;
    will-change: transform;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -8px -28px;
    border-radius: 100px;
    background: #e185ff;
    z-index: -2;
    opacity: 0;
    transform: scale(0.6);
    transition: transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.35s ease;
    will-change: transform;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

.btn-cta-arrow {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin-left: 12px;
  object-fit: contain;
}

@keyframes arrowBounce {
  0% { transform: translateX(0); }
  40% { transform: translateX(8px); }
  100% { transform: translateX(0); }
}

.btn-cta:hover {
  color: #fff;
  text-decoration: none;
  box-shadow:
    0 0 0 4px #1e00ff,
    -4px 0 0 4px #1e00ff,
    4px 0 0 4px #1e00ff;

  &::before {
    opacity: 1;
    transform: scale(1);
  }

  &::after {
    opacity: 1;
    transform: scale(1);
  }

  .btn-cta-arrow {
    animation: arrowBounce 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }
}

.btn-cta-hidden {
  pointer-events: none;
  opacity: 0;
  transform: translateY(12px);
}

.hero-media {
  position: absolute;
  top: 50%;
  right: 0;
  width: 517px;
  height: 549px;
  border-radius: 48px;
  overflow: hidden;
  background: transparent;
  transform: translateY(-50%);
}

.hero-poster,
.hero-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: transparent;
}

.hero-poster {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.hero-poster.is-hidden {
  opacity: 0;
}

@keyframes blink {
  50% {
    opacity: 0.2;
  }
}

@media (max-width: 960px) {
  .hero {
    height: auto;
    min-height: auto;
  }

  .hero-inner {
    display: block;
    width: auto;
    max-width: none;
    height: auto;
    padding: 100px 20px 48px;
  }

  .hero-title-1,
  .hero-title-2-wrap {
    font-size: 36px;
    min-height: auto;
  }

  .hero-title-2-wrap {
    display: block;
    max-width: 100%;
    margin-bottom: 24px;
    overflow: hidden;
    white-space: nowrap;
    /* 10 字单行：按可用宽度缩放，放得下时不超过 36px */
    font-size: min(36px, calc((100vw - 40px) / 10));
  }

  .hero-title-2 {
    display: inline-block;
    max-width: 100%;
    white-space: nowrap;
  }

  .hero-tags {
    margin-bottom: 32px;
    font-size: 14px;
    gap: 8px;
  }

  .btn-cta {
    min-width: 148px;
    height: 48px;
    font-size: 18px;
  }

  .hero-media {
    position: relative;
    top: auto;
    right: auto;
    width: 100%;
    height: auto;
    aspect-ratio: 517 / 549;
    margin-top: 32px;
    transform: none;
  }

  .hero-video {
    position: relative;
    z-index: 1;
  }
}
</style>
